import { Injectable, OnDestroy } from '@angular/core';
import { TelemetryBackendClientService } from '../telemetry/telemetry-client.service';
import { environment } from '../environments/environment';
import { LogEvent } from '../telemetry/telemetry-service.protocol';
import { TelemetryEvent } from '../telemetry/telementry-event.class';
import { diagnosticId, EssDiagnosticEvent } from './ess-diagnostic-event';
import { EssDiagnosticQueue } from './ess-diagnostic-queue';
import { IndexedDbEssDiagnosticStore, StoredEssDiagnostic } from './ess-diagnostic-store';
import { ESS_CONNECTION_DIAGNOSTICS_ENABLED } from './ess-diagnostics.build-settings';

@Injectable({ providedIn: 'root' })
export class EssConnectionDiagnosticsService implements OnDestroy {
    private queue: EssDiagnosticQueue;
    private timer: any;
    private startup: Promise<void>;
    private loginIdentity: string;
    private sessionId: string;

    constructor(private backend: TelemetryBackendClientService) {}

    ngOnDestroy(): void { clearInterval(this.timer); }

    private enabled(): boolean {
        if (!ESS_CONNECTION_DIAGNOSTICS_ENABLED) { return false; }
        const host = new URL(environment.eventStreamUrl).hostname;
        let enabled = host === 'tradingstagingserver.net' || host.endsWith('.tradingstagingserver.net');
        try { enabled = enabled || (host === 'localhost' && localStorage.getItem('ess-diagnostics.local-enabled') === 'true'); }
        catch (_) { /* Storage denied: staging still collects in memory. */ }
        return enabled;
    }

    start(): Promise<void> {
        if (!ESS_CONNECTION_DIAGNOSTICS_ENABLED) {
            if (!this.startup) { this.startup = this.clearDisabledDiagnostics(); }
            return this.startup;
        }
        if (!this.enabled()) { return Promise.resolve(); }
        if (!this.queue) {
            this.queue = new EssDiagnosticQueue(new IndexedDbEssDiagnosticStore(), environment.telemetryServiceUrl,
                records => this.send(records));
            this.timer = setInterval(() => this.queue.flush(), 5000);
            this.startup = this.migrateLegacy().then(() => this.queue.flush());
        }
        return this.startup;
    }

    private async clearDisabledDiagnostics(): Promise<void> {
        // Cleanup is best effort; failure must never enable collection or delivery.
        // A fresh app startup retries cleanup without retaining a delivery timer.
        try { sessionStorage.removeItem('ess-diagnostics.queue'); } catch (_) {}
        try { await new IndexedDbEssDiagnosticStore().clear(); } catch (_) {}
    }

    beginSession(loginIdentity?: string): string {
        if (!this.enabled()) { return null; }
        this.start();
        if (!this.sessionId || this.loginIdentity !== loginIdentity) {
            this.sessionId = diagnosticId();
            this.loginIdentity = loginIdentity;
        }
        return this.sessionId;
    }

    record(event: EssDiagnosticEvent): void {
        if (!this.queue || !event || event.schemaVersion !== 1) { return; }
        this.queue.enqueue(this.stored(event, environment.productVersion));
    }

    private stored(event: EssDiagnosticEvent, appVersion: string): StoredEssDiagnostic {
        return { eventId: event.eventId, event, destination: environment.telemetryServiceUrl,
            appId: environment.runtimeAppId, appVersion };
    }

    private async migrateLegacy(): Promise<void> {
        const key = 'ess-diagnostics.queue';
        try {
            const raw = sessionStorage.getItem(key);
            if (!raw) { return; }
            const saved = JSON.parse(raw);
            if (!Array.isArray(saved.events) || saved.events.some(event => !event || event.schemaVersion !== 1 || !event.eventId)) {
                throw new Error('Invalid legacy diagnostics');
            }
            const versions = new Map<string, string>();
            saved.events.forEach(event => {
                if (event.eventType === 'session-start' && event.details.appVersion) {
                    versions.set(event.sessionId, event.details.appVersion);
                }
            });
            const records = saved.events.map(event => this.stored(event, versions.get(event.sessionId) || 'unknown'));
            const context = saved.events[saved.events.length - 1];
            if (context && saved.pendingGap && Object.keys(saved.pendingGap).some(name => saved.pendingGap[name] > 0)) {
                const eventId = context.eventId + ':legacy-gap';
                records.push(this.stored({ schemaVersion: 1, eventId, sessionId: context.sessionId,
                    attemptId: context.attemptId, source: 'collector', sequence: 0,
                    collectorEpoch: 'durable-v1',
                    timestamp: new Date().toISOString(), elapsedMs: null, eventType: 'collection-gap',
                    details: Object.assign({ scope: 'legacy-tab-queue' }, saved.pendingGap) }, versions.get(context.sessionId) || 'unknown'));
            }
            await this.queue.migrate(records, () => {
                if (sessionStorage.getItem(key) === raw) { sessionStorage.removeItem(key); }
            });
        } catch (_) {
            // Preserve unreadable snapshots; never erase old evidence on a failed migration.
            console.warn('Existing ESS diagnostic queue could not be read; it was preserved.');
        }
    }

    private send(pending: StoredEssDiagnostic[]): Promise<any> {
        const records = pending.map(record => ({ timestamp: new Date(record.event.timestamp),
            appId: record.appId, appVersion: record.appVersion,
            authToken: null, eventType: 'ess-connection', key: record.eventId,
            data: JSON.stringify(record.event) } as TelemetryEvent));
        const command = new LogEvent(records);
        return this.backend.processCommand(command, 10000);
    }
}
